import { LS_HEADER_CACHE_PREFIX } from 'constants/local-storage';

import { getEnv } from 'helpers/env';

import { isValidContract, isValidPublicKey } from 'services/stellar/utils/validators';

import { HeaderAccount, HeaderProposal, ProposalCounts } from './Header.types';

const MAX_AGE = 24 * 60 * 60 * 1000;
const cacheKey = (key: string) => `${LS_HEADER_CACHE_PREFIX}${getEnv()}:${key}`;

// These snapshots only render previews. Every mount revalidates public API data;
// neither account balances nor authentication/session tokens are stored here.
export const readHeaderCache = <T>(
    key: string,
    validate: (value: unknown) => value is T,
): T | null => {
    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey(key)));
        const age = Date.now() - cached?.updatedAt;
        return Number.isFinite(age) && age >= 0 && age < MAX_AGE && validate(cached.value)
            ? cached.value
            : null;
    } catch {
        return null;
    }
};

export const writeHeaderCache = (key: string, value: unknown) => {
    try {
        localStorage.setItem(cacheKey(key), JSON.stringify({ updatedAt: Date.now(), value }));
    } catch {
        // Storage can be unavailable or full; the live header remains usable.
    }
};

export const isProposalCounts = (value: unknown): value is ProposalCounts => {
    const counts = value as ProposalCounts;
    return Boolean(
        counts &&
        Number.isSafeInteger(counts.active) &&
        counts.active >= 0 &&
        Number.isSafeInteger(counts.discussion) &&
        counts.discussion >= 0,
    );
};

export const isHeaderProposal = (value: unknown): value is HeaderProposal => {
    const proposal = value as HeaderProposal;
    return Boolean(
        proposal &&
        Number.isSafeInteger(proposal.id) &&
        ['ADD_ASSET', 'REMOVE_ASSET'].includes(proposal.proposal_type) &&
        typeof proposal.end_at === 'string' &&
        Date.parse(proposal.end_at) > Date.now() &&
        (proposal.image === undefined ||
            proposal.image === null ||
            typeof proposal.image === 'string') &&
        ((typeof proposal.asset_code === 'string' &&
            /^[a-zA-Z0-9]{1,12}$/.test(proposal.asset_code) &&
            typeof proposal.asset_issuer === 'string' &&
            isValidPublicKey(proposal.asset_issuer)) ||
            (typeof proposal.asset_contract_address === 'string' &&
                isValidContract(proposal.asset_contract_address))),
    );
};

export const isHeaderAccount = (value: unknown): value is HeaderAccount => {
    const account = value as HeaderAccount;
    return Boolean(
        account &&
        typeof account.pubKey === 'string' &&
        isValidPublicKey(account.pubKey) &&
        typeof account.federation === 'string',
    );
};
